import React from "react";
import { cn } from "@/lib/utils";
import { selectClass } from "@/lib/formStyles";
import { DoctorOption } from "../../hooks/queries/useDoctors";

/**
 * Éléments communs aux fenêtres de la liste d'attente (ajout, modification,
 * retrait) : titre, choix du médecin et pied de fenêtre.
 */

type QueueModalTitleProps = {
  eyebrow: string;
  title: React.ReactNode;
};

export const QueueModalTitle: React.FC<QueueModalTitleProps> = ({ eyebrow, title }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">{eyebrow}</p>
    <h3 className="mt-1 text-xl font-semibold text-slate-900">{title}</h3>
  </div>
);

type DoctorSelectProps = {
  doctors: DoctorOption[];
  value: string;
  onChange: (doctorId: string) => void;
  /** Option vide en tête de liste ; absente quand un médecin est déjà assigné. */
  placeholder?: string;
  disabled?: boolean;
  label?: string;
};

export const DoctorSelect: React.FC<DoctorSelectProps> = ({
  doctors,
  value,
  onChange,
  placeholder,
  disabled,
  label = "Médecin assigné",
}) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <div className="relative">
      <select
        className={selectClass}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {placeholder != null && <option value="">{placeholder}</option>}
        {doctors.map((doctor) => (
          <option key={doctor.id} value={doctor.id}>
            {doctor.firstName} {doctor.lastName}
          </option>
        ))}
      </select>
      <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
    </div>
  </label>
);

const CONFIRM_VARIANT_CLASSES = {
  primary: "bg-green-600 hover:bg-green-700 active:bg-green-800 shadow-green-600/25",
  danger: "bg-rose-600 hover:bg-rose-700 active:bg-rose-800 shadow-rose-600/25",
} as const;

type QueueModalActionsProps = {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  /** Classe Boxicons de l'icône du bouton de confirmation (ex. `bx-check`). */
  confirmIcon: string;
  confirmDisabled: boolean;
  isLoading: boolean;
  variant?: keyof typeof CONFIRM_VARIANT_CLASSES;
};

export const QueueModalActions: React.FC<QueueModalActionsProps> = ({
  onCancel,
  onConfirm,
  confirmLabel,
  confirmIcon,
  confirmDisabled,
  isLoading,
  variant = "primary",
}) => (
  <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
    <button
      type="button"
      onClick={onCancel}
      disabled={isLoading}
      className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
    >
      Annuler
    </button>
    <button
      type="button"
      disabled={confirmDisabled}
      onClick={onConfirm}
      className={cn(
        "flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl text-white text-sm font-medium shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed",
        CONFIRM_VARIANT_CLASSES[variant]
      )}
    >
      <i className={cn("bx text-base", confirmIcon)} /> {confirmLabel}
    </button>
  </div>
);
